import React from 'react';
import { Shield, CheckCircle, AlertCircle, Key } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

/**
 * Security tab — bundles 2FA management and Change Password forms.
 */
const SecurityTab = ({
  twoFA,
  setupData,
  setSetupData,
  verifyCode,
  setVerifyCode,
  disableCode,
  setDisableCode,
  onSetup2FA,
  onVerifySetup2FA,
  onDisable2FA,
  settingUp2FA,
  passwordForm,
  setPasswordForm,
  onChangePassword,
  changingPassword,
}) => (
  <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" /> Two-Factor Authentication
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {twoFA.loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : twoFA.enabled ? (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-green-800">2FA is enabled</p>
                <p className="text-sm text-green-600">Your account is protected with two-factor authentication.</p>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">To disable 2FA, enter a code from your authenticator app:</p>
              <div className="flex gap-2">
                <Input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-40 text-center font-mono text-lg"
                  maxLength={6}
                  data-testid="disable-2fa-code"
                />
                <Button variant="destructive" onClick={onDisable2FA} disabled={disableCode.length !== 6} data-testid="disable-2fa-btn">
                  Disable 2FA
                </Button>
              </div>
            </div>
          </>
        ) : setupData ? (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="font-semibold text-blue-800 mb-2">Scan this QR code with your authenticator app:</p>
              <p className="text-sm text-blue-600 mb-4">Use Google Authenticator, Authy, or any TOTP app.</p>
              <div className="flex justify-center mb-4">
                <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48 border rounded-lg" data-testid="2fa-qr-code" />
              </div>
              <div className="bg-white rounded p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">Manual entry key:</p>
                <code className="text-sm font-mono select-all bg-gray-100 px-3 py-1 rounded">{setupData.secret}</code>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Enter the 6-digit code from your app to verify:</p>
              <div className="flex gap-2">
                <Input
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-40 text-center font-mono text-lg"
                  maxLength={6}
                  autoFocus
                  data-testid="verify-2fa-code"
                />
                <Button onClick={onVerifySetup2FA} disabled={verifyCode.length !== 6} className="bg-green-600 hover:bg-green-700" data-testid="verify-2fa-btn">
                  Verify & Enable
                </Button>
              </div>
            </div>
            <button onClick={() => setSetupData(null)} className="text-sm text-gray-500 hover:text-gray-700">
              Cancel setup
            </button>
          </>
        ) : (
          <>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-yellow-800">2FA is not enabled</p>
                <p className="text-sm text-yellow-600">Add an extra layer of security to your admin account.</p>
              </div>
            </div>
            <Button onClick={onSetup2FA} disabled={settingUp2FA} className="bg-green-600 hover:bg-green-700" data-testid="setup-2fa-btn">
              <Shield className="h-4 w-4 mr-2" /> {settingUp2FA ? 'Setting up...' : 'Setup Two-Factor Authentication'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" /> Change Password
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
          <Input
            type="password"
            value={passwordForm.current}
            onChange={(e) => setPasswordForm((f) => ({ ...f, current: e.target.value }))}
            placeholder="Enter current password"
            data-testid="current-password-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
          <Input
            type="password"
            value={passwordForm.new_password}
            onChange={(e) => setPasswordForm((f) => ({ ...f, new_password: e.target.value }))}
            placeholder="Minimum 8 characters"
            data-testid="new-password-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
          <Input
            type="password"
            value={passwordForm.confirm}
            onChange={(e) => setPasswordForm((f) => ({ ...f, confirm: e.target.value }))}
            placeholder="Confirm new password"
            data-testid="confirm-password-input"
          />
        </div>
        <Button
          onClick={onChangePassword}
          disabled={changingPassword || !passwordForm.current || !passwordForm.new_password || !passwordForm.confirm}
          className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
          data-testid="change-password-btn"
        >
          {changingPassword ? 'Changing...' : 'Change Password'}
        </Button>
        <p className="text-xs text-gray-400">After changing your password, remove the ADMIN_DEFAULT_PASSWORD from your deployment secrets for security.</p>
      </CardContent>
    </Card>
  </>
);

export default SecurityTab;
